import { Connection } from '@solana/web3.js';
import { CONFIG } from '../config';

let endpoints = [CONFIG.RPC_URL, CONFIG.SOL_RPC_URL].filter(Boolean);
let currentIndex = 0;

function getCurrentEndpoint() {
  if (!endpoints.length) throw new Error('No RPC endpoints configured');
  return endpoints[currentIndex % endpoints.length];
}

function switchEndpoint() {
  currentIndex = (currentIndex + 1) % endpoints.length;
}

function isRateOrAuthError(err) {
  const msg = String(err && (err.message || err.toString() || ''));
  const code = err && (err.status || err.code);
  // Rate limit
  if (code === 429) return true;
  if (/\b429\b/.test(msg)) return true;
  if (/rate\s*limit/i.test(msg)) return true;
  // Auth / API key issues
  if (code === 401) return true;
  if (/\b401\b/.test(msg)) return true;
  if (/invalid\s*api\s*key/i.test(msg)) return true;
  return false;
}

async function callWithFailover(method, args, attempt = 0, maxAttempts = 2) {
  const endpoint = getCurrentEndpoint();
  const conn = new Connection(endpoint, 'confirmed');
  try {
    return await conn[method](...args);
  } catch (err) {
    const shouldFailover = isRateOrAuthError(err) || (err && err.name === 'FetchError');
    if (shouldFailover && attempt + 1 < maxAttempts) {
      switchEndpoint();
      return callWithFailover(method, args, attempt + 1, maxAttempts);
    }
    throw err;
  }
}

const failoverConnection = new Proxy({}, {
  get: (_, prop) => {
    if (typeof Connection.prototype[prop] === 'function') {
      return (...args) => callWithFailover(prop, args);
    }
    return undefined;
  }
});

// Helper for SDKs that require a raw endpoint string (e.g., StablebondProgram)
async function withRpcEndpointFailover(fn, maxAttempts = 2) {
  let attempt = 0;
  let lastErr;
  while (attempt < maxAttempts) {
    try {
      const endpoint = getCurrentEndpoint();
      return await fn(endpoint);
    } catch (err) {
      lastErr = err;
      if (isRateOrAuthError(err) && attempt + 1 < maxAttempts) {
        switchEndpoint();
        attempt++;
        continue;
      }
      throw err;
    }
  }
  throw lastErr;
}

export { failoverConnection, withRpcEndpointFailover, getCurrentEndpoint };
